import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';

import { SearchStateService } from '../../../shared/services/search-state.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { LocationSearchFilters } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';
import { SearchFilter } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { PostGetLocationSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetLocationSearchRequest';
import { TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';

interface FilterOption {
  label: string;
  value: string;
  selected: boolean;
}

@Component({
  selector: 'app-location-search-filter-mobile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatIconModule
  ],
  templateUrl: './location-search-filter.component.html',
  styleUrls: ['./location-search-filter.component.css']
})
export class LocationSearchFilterComponent implements OnInit, OnDestroy {
  @Input() isPlus: boolean = false;
  @Input() currentLocation: LocationCoordinates | null = null;

  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  locale = "en-US";
  isOpen = false;
  locationQuery: string = '';
  serviceSearchText: string = '';
  locationTypeSearchQuery: string = '';

  locationNameLabel = "Location name";
  serviceLabel = "Service";
  typeLabel = "Location Type";
  placeholderText = "Search";
  clearAllBtnText = "Clear all"
  applyBtnText = "Apply"
  
  // Service autocomplete
  filteredServices$!: Observable<SearchFilter[]>;
  allServices: SearchFilter[] = [];
  selectedServices: SearchFilter[] = [];
  serviceCount: number = 0;

  sortMethod: 'distance' | 'availability' | 'name' = 'availability';
  currentFilters: LocationSearchFilters | null = null;
  queryFacilityOrLocationName: string = '';
  queryCityOrZip: string = ''

  serviceOptions: FilterOption[] = [
    { label: 'Radiology', value: 'radiology', selected: false },
    { label: 'Laboratory', value: 'laboratory', selected: true },
    { label: 'Ultrasound', value: 'ultrasound', selected: false },
    { label: 'MRI', value: 'mri', selected: false },
    { label: 'X-Ray', value: 'xray', selected: false }
  ];

  locationTypeOptions: FilterOption[] = [
    { label: 'Urgent Care', value: 'urgent_care', selected: true },
    { label: 'MemorialCare Medical Group', value: 'medical_group', selected: false },
    { label: 'Health Center', value: 'health_center', selected: false },
    { label: 'Hospital', value: 'hospital', selected: false },
    { label: 'Imaging Center', value: 'imaging', selected: false }
  ];

  private destroy$ = new Subject<void>();

  constructor(private translate: TranslateService) {
    
  }

  ngOnInit(): void {
    this.locale = getLocaleFromUrl();
    this.translate.use(this.locale);

    this.translate.get('FILTERS.LOCATION_NAME').subscribe(translated => {
      this.locationNameLabel = translated;
    });

    this.translate.get('FILTERS.LOCATION_SERVICE').subscribe(translated => {
      this.serviceLabel = translated;
    });
    this.translate.get('FILTERS.LOCATION_TYPE').subscribe(translated => {
      this.typeLabel = translated;
    });
    this.translate.get('CLEAR_ALL').subscribe(translated => {
      this.clearAllBtnText = translated;
    });

    this.translate.get('APPLY').subscribe(translated => {
      this.applyBtnText = translated;
    });

    this.translate.get('SEARCH').subscribe(translated => {
      this.placeholderText = translated;
    });
    
    // Subscribe to filter toggle state
    this.searchState.locationSearchFilterToggle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.isOpen = isOpen;
      });

    // Subscribe to location search query
    this.searchState.locationSearchQuery$
      .pipe(takeUntil(this.destroy$))
      .subscribe(query => {
        if (query) {
          this.queryFacilityOrLocationName = query.facilityOrLocationName || '';
          this.queryCityOrZip = query.cityOrZip || '';
        }
      });

    // Subscribe to location search filters
    this.searchState.locationSearchFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(filters => {
        if (filters) {
          this.currentFilters = filters;
          // initialize location name from filters
          if (filters.facilityOrLocationName) {
            this.locationQuery = filters.facilityOrLocationName;
          }
          // Initialize services from filters
          if (filters.serviceList) {
            this.allServices = filters.serviceList;
            this.selectedServices = filters.serviceList.filter(s => s.isSelected);
            this.serviceCount = this.selectedServices.length;
            this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
          }

          // Initialize location types from filters
          if (filters.typeList) {
            this.locationTypeOptions = filters.typeList.map(t => ({
              label: t.value,
              value: t.value,
              selected: t.isSelected
            }));
          }
        }
      });

    this.searchState.locationSortMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(method => {
        this.sortMethod = method;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeFilter(): void {
    this.searchState.setLocationSearchFilterToggle(false);
  }

  toggleOption(type: 'service' | 'locationType', option: FilterOption): void {
    option.selected = !option.selected;
  }

  // Service Autocomplete Methods
  filterServices(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    } else {
      const filterValue = searchText.toLowerCase();
      this.filteredServices$ = of(
        this.allServices.filter(s => 
          !s.isSelected && s.value?.toLowerCase().includes(filterValue)
        )
      );
    }
  }

  onServiceSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const service = this.allServices.find(s => s.value === selectedValue);

    if (service && !service.isSelected) {
      service.isSelected = true;
      this.selectedServices = this.allServices.filter(s => s.isSelected);
      this.serviceCount = this.selectedServices.length;
      this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    }

    // Clear the search text after selection
    setTimeout(() => {
      this.serviceSearchText = '';
      this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    }, 0);
  }

  removeService(service: SearchFilter): void {
    service.isSelected = false;
    this.selectedServices = this.allServices.filter(s => s.isSelected);
    this.serviceCount = this.selectedServices.length;
    this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
  }

  getFilteredLocationTypes(): FilterOption[] {
    const filtered = this.locationTypeOptions.filter(option =>
      option.label.toLowerCase().includes(this.locationTypeSearchQuery.toLowerCase())
    );
    return filtered;
  }

  getServiceSelectedCount(): number {
    return this.serviceCount;
  }

  getLocationTypeSelectedCount(): number {
    return this.locationTypeOptions.filter(opt => opt.selected).length;
  }

  getSelectedCount(): number {
    return this.getServiceSelectedCount() + this.getLocationTypeSelectedCount();
  }

  onLocationTypeChange(option: FilterOption): void {
    option.selected = !option.selected;
  }

  clearFilters(): void {
    this.allServices.forEach(s => s.isSelected = false);
    this.selectedServices = [];
    this.serviceCount = 0;
    this.locationTypeOptions.forEach(opt => opt.selected = false);
    this.locationQuery = '';
    this.serviceSearchText = '';
    this.locationTypeSearchQuery = '';
    this.filteredServices$ = of(this.allServices);
  }

  applyFilters(): void {
    console.log('Applying Filters:', this.currentFilters);
    this.searchState.setShowSpinner(true);

    // Build search request with all filters
    var request = new PostGetLocationSearchRequest();

    // Set facility/location name from Location text input
    request.facilityOrLocationName = this.locationQuery;
    
    request.cityOrZip = this.queryCityOrZip;
    request.distanceInMiles = this.currentFilters?.distanceInMiles ?? 15;

    // Apply selected services and location types
    request.services = this.allServices.filter(s => s.isSelected).map(s => s.value).join(', ');
    request.types = this.locationTypeOptions.filter(t => t.selected).map(t => t.value).join(', ');

    // Only set the latitude and longitude if a cityOrZip value is not provided
    if ((request.cityOrZip == null || request.cityOrZip == '') && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude) {
      request.latitude = this.currentLocation.latitude;
      request.longitude = this.currentLocation.longitude;
    }

    console.log("Location Search Request:", request);

    this.mcAppGateway.PostGetLocationSearch(request).subscribe({
      next: (response) => {
        var sortedResponse = this.searchState.sortLocationSearchResults(response || [], this.sortMethod);
        console.log('Location Search Response (after applying filters):', sortedResponse);
        this.searchState.setLocationSearchResults(sortedResponse);
        
        // Update filters with new selections
        if (this.currentFilters) {
          this.currentFilters.facilityOrLocationName = this.locationQuery;
          this.currentFilters.serviceList = this.allServices;
          this.currentFilters.typeList = this.locationTypeOptions.map(t => ({
            value: t.value,
            isSelected: t.selected
          }));
          this.searchState.setLocationSearchFilters(this.currentFilters);
        }

        this.searchState.setLocationSearchQuery({
          facilityOrLocationName: this.locationQuery,
          cityOrZip: this.queryCityOrZip
        });

        this.searchState.setShowSpinner(false);
        this.searchState.setLocationSearchFilterToggle(false);
      },
      error: (error) => {
        console.error('An error occurred while searching for locations with filters: ', error);
        alert('An error occurred while searching for locations with filters: ' + error.message);
        
        this.searchState.setShowSpinner(false);
      },
    });
  }
}
